"use client"

import { useState, useEffect } from "react"
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { Cookie, X } from "lucide-react"
import Link from "next/link"
import { motion, AnimatePresence } from "framer-motion"

export function CookieConsentBanner() {
  const [isVisible, setIsVisible] = useState(false)
  const [showDetails, setShowDetails] = useState(false)
  const [preferences, setPreferences] = useState({
    analytics: true,
    errorTracking: true,
  })

  useEffect(() => {
    // Check if user has already consented
    const hasConsented = localStorage.getItem("cookie-consent")
    if (!hasConsented) {
      // Show banner after a short delay
      setTimeout(() => setIsVisible(true), 1000)
    }
  }, [])

  const handleAcceptAll = async () => {
    await savePreferences(true, true)
    setIsVisible(false)
  }

  const handleAcceptNecessary = async () => {
    await savePreferences(false, false)
    setIsVisible(false)
  }

  const handleSavePreferences = async () => {
    await savePreferences(preferences.analytics, preferences.errorTracking)
    setIsVisible(false)
  }

  const savePreferences = async (analytics: boolean, errorTracking: boolean) => {
    // Save to localStorage
    localStorage.setItem("cookie-consent", JSON.stringify({ analytics, errorTracking, timestamp: Date.now() }))

    // Save to database if user is authenticated
    try {
      await fetch("/api/user/cookie-preferences", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          cookieAnalyticsConsent: analytics,
          cookieErrorTrackingConsent: errorTracking,
        }),
      })
    } catch (error) {
      // User might not be authenticated yet, that's okay
      console.log("Cookie preferences saved locally")
    }
  }

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6"
        >
          <Card className="mx-auto max-w-4xl border-2 shadow-2xl">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <Cookie className="h-6 w-6 text-primary" />
                  <CardTitle>Soubory cookie</CardTitle>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setIsVisible(false)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>

            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Používáme cookies k zajištění funkčnosti aplikace a ke zlepšení vašeho zážitku. Některé cookies
                vyžadují váš souhlas.
              </p>

              {showDetails && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="space-y-4 border-t pt-4"
                >
                  <div>
                    <h4 className="mb-2 text-sm font-semibold">Nezbytné cookies (vždy aktivní)</h4>
                    <p className="text-xs text-muted-foreground">
                      Clerk session cookies pro přihlášení. Tyto cookies jsou nezbytné pro fungování aplikace.
                    </p>
                  </div>

                  <div className="flex items-start space-x-3">
                    <Checkbox
                      id="analytics"
                      checked={preferences.analytics}
                      onCheckedChange={(checked) =>
                        setPreferences((prev) => ({ ...prev, analytics: checked === true }))
                      }
                    />
                    <div className="grid gap-1.5 leading-none">
                      <Label htmlFor="analytics" className="text-sm font-medium cursor-pointer">
                        Analytické cookies
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        Vercel Analytics – anonymní statistiky návštěvnosti pro zlepšení aplikace
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <Checkbox
                      id="errorTracking"
                      checked={preferences.errorTracking}
                      onCheckedChange={(checked) =>
                        setPreferences((prev) => ({ ...prev, errorTracking: checked === true }))
                      }
                    />
                    <div className="grid gap-1.5 leading-none">
                      <Label htmlFor="errorTracking" className="text-sm font-medium cursor-pointer">
                        Sledování chyb
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        Sentry – detekce a oprava technických problémů
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}

              <div className="flex gap-2 text-xs text-muted-foreground">
                <Link href="/cookies" className="hover:text-primary hover:underline">
                  Zásady cookies
                </Link>
                <span>•</span>
                <Link href="/privacy" className="hover:text-primary hover:underline">
                  Ochrana údajů
                </Link>
              </div>
            </CardContent>

            <CardFooter className="flex flex-col gap-2 sm:flex-row">
              {!showDetails ? (
                <>
                  <Button onClick={handleAcceptAll} className="w-full sm:flex-1">
                    Přijmout vše
                  </Button>
                  <Button onClick={handleAcceptNecessary} variant="outline" className="w-full sm:flex-1">
                    Pouze nezbytné
                  </Button>
                  <Button
                    onClick={() => setShowDetails(true)}
                    variant="ghost"
                    className="w-full sm:w-auto"
                  >
                    Nastavit
                  </Button>
                </>
              ) : (
                <>
                  <Button onClick={handleSavePreferences} className="w-full sm:flex-1">
                    Uložit preference
                  </Button>
                  <Button
                    onClick={() => setShowDetails(false)}
                    variant="outline"
                    className="w-full sm:w-auto"
                  >
                    Zpět
                  </Button>
                </>
              )}
            </CardFooter>
          </Card>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
